import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/utils/styles';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@design-system/Collapsible';
import { useContextSwitcher } from '@/hooks/context/useContextSwitcher';

/**
 * Mobile counterpart of the sidebar experience switcher (DE-336): the sidebar
 * is desktop-only, so below `md` the Profile > Information list carries this
 * expanding row instead.
 */
export const SettingsPageContextSwitcherItem = () => {
  const [open, setOpen] = useState(false);
  const { canSwitch, options, currentValue, currentLabel, title, selectValue } =
    useContextSwitcher({ includeConsumerKitToggle: true });

  if (!canSwitch) return null;

  return (
    <li
      className="relative text-primary border-b border-stroke-secondary-subtle md:hidden"
      data-testid={`settingsSupport-item-${title.toLowerCase().replace(/\s+/g, '')}`}
    >
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger
          className="h-12 flex flex-row justify-start items-center px-5 w-full gap-4 typography-btn-large text-primary"
          aria-label="Switch experience"
          data-testid="settingsSupport-contextSwitcher"
        >
          {title}
          <span className="ml-auto flex items-center gap-2 text-tertiary">
            <span className="typography-caption !font-semilight !text-primary">{currentLabel}</span>
            <ChevronDown
              width={20}
              height={20}
              aria-hidden
              className={cn('transition-transform', open && 'rotate-180')}
            />
          </span>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <ul className="pb-2">
            {options.map(({ value, label, showLatest }) => (
              <li key={value}>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    selectValue(value);
                  }}
                  className={cn(
                    'h-12 flex flex-row items-center gap-3 px-5 w-full text-left typography-body text-primary',
                    value === currentValue && 'bg-background-secondary',
                  )}
                  data-testid={`settingsSupport-contextSwitcher-option-${value}`}
                >
                  {label}
                  {showLatest && (
                    <span className="rounded-full border border-stroke-secondary-subtle px-2.5 py-0.5 typography-caption !font-semilight !text-primary">
                      Latest
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </CollapsibleContent>
      </Collapsible>
    </li>
  );
};
